import { useCallback, useMemo } from 'react';
import isNil from 'lodash/isNil';
import isEmpty from 'lodash/isEmpty';

import {
    EhrChatData,
    Optional,
    RecurringSubscriptionData,
    SubscriptionOrderData,
} from 'chaika-frontend-types/frontend-cms';
import { RecurringSubscriptionStatus, SubscriptionOrderStatus } from 'chaika-enums';

import { store } from '@store';

import { translate } from '@chaika/i18n/helper';

import { useEhrSubscription } from '@modules/patient/hooks/useEhrSubscription';
import { isChatWithDoctor } from '@patient/utils/isChatWithDoctor';

import { MeatballButtonMenuItem } from '@components/ActionButtons/MeatballButton';

interface Params {
    patientId: string;
    chats: EhrChatData[];
    subscriptionOrders: SubscriptionOrderData[];
    recurringSubscriptions: RecurringSubscriptionData[];
    onChatCreation(): void;
}

interface Options {
    options: MeatballButtonMenuItem[];
}

export const useDoctorChatOptions = ({
    patientId,
    chats,
    subscriptionOrders,
    recurringSubscriptions,
    onChatCreation,
}: Params): Options => {
    const { authUser } = store;
    const { doPatientHasActiveSubscriptions, createTrialSubscription } = useEhrSubscription();

    const openChat = useCallback(
        async (topicUrl: Optional<string>, doctorId: string) => {
            const hasActiveSubscriptions = await doPatientHasActiveSubscriptions(patientId);

            if (!hasActiveSubscriptions) {
                await createTrialSubscription({ doctorId, patientId });
            }

            window.open(topicUrl, '_blank');
        },
        [patientId, doPatientHasActiveSubscriptions, createTrialSubscription],
    );

    const currentDoctorChat = useMemo((): Optional<EhrChatData> => {
        const recurringSubscriptionChatLink = recurringSubscriptions.find(
            subscription =>
                subscription.doctorId === authUser.id &&
                subscription.status === RecurringSubscriptionStatus.Active &&
                !isEmpty(subscription.chatLink),
        )?.chatLink;

        if (!isNil(recurringSubscriptionChatLink)) {
            return { patientId, doctorId: authUser.id, joinLink: recurringSubscriptionChatLink };
        }

        let currentDoctorChat = chats.find(chat => isChatWithDoctor(chat, authUser.id));

        if (isNil(currentDoctorChat)) {
            const chat = subscriptionOrders
                .filter(subscription => {
                    return (
                        SubscriptionOrderStatus.Active === subscription.status &&
                        subscription.doctorId === authUser.id &&
                        !isEmpty(subscription.chats)
                    );
                })
                .flatMap(subscription => subscription.chats)
                .find(chat => chat.doctorId === authUser.id);

            if (!isNil(chat)) {
                currentDoctorChat = {
                    patientId,
                    doctorId: chat.doctorId,
                    joinLink: chat.doctorChatLink,
                };
            }
        }

        return currentDoctorChat;
    }, [chats, subscriptionOrders, recurringSubscriptions, authUser.id, patientId]);

    const options = useMemo((): MeatballButtonMenuItem[] => {
        if (!isNil(currentDoctorChat)) {
            return [
                {
                    label: `${translate('doctorsChat')} (${authUser.initials})`,
                    onClick: () =>
                        openChat(currentDoctorChat.joinLink ?? currentDoctorChat.registrationLink, authUser.id),
                },
            ];
        }

        return [
            {
                label: isEmpty(chats) ? translate('sendContactsButtonLabel') : translate('createChat'),
                onClick: onChatCreation,
            },
        ];
    }, [chats, currentDoctorChat, authUser.id, authUser.initials, openChat, onChatCreation]);

    return {
        options,
    };
};
